import { ACCOUNT_CONTEXT_TOOL, SYMPHONY_ACTIONS, runSymphonyAction, type SymphonyHost } from "@/symphony/actions";
import {
  SYMPHONY_CHANNEL,
  isAllowedSymphonyMessageOrigin,
  type SpeakResultMessage,
  type SymphonyRequestMessage,
} from "@/symphony/contract";
import type { SymphonyAccountContext } from "../../supabase/functions/_shared/symphonyAccountContext";
import { readSymphonyClient } from "@/symphony/session";

export interface SpeakSymphonyBridge {
  version: 1;
  actions: typeof SYMPHONY_ACTIONS;
  dispatch: (name: string, args?: unknown) => Promise<unknown>;
  getAccountContext: () => Promise<SymphonyAccountContext>;
  registerClientTools?: () => void;
}

function isRequest(value: unknown): value is SymphonyRequestMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<SymphonyRequestMessage>;
  return message.source === "symphony"
    && message.channel === SYMPHONY_CHANNEL
    && typeof message.requestId === "string"
    && (message.type === "action" || message.type === "account.context");
}

export function createSymphonyBridge(options: {
  host: SymphonyHost;
  gateway: string;
  getAccountContext: () => Promise<SymphonyAccountContext>;
}): () => void {
  const bridge: SpeakSymphonyBridge = {
    version: 1,
    actions: SYMPHONY_ACTIONS,
    dispatch: (name, args) => runSymphonyAction(name, args, options.host),
    getAccountContext: options.getAccountContext,
  };

  const hostWindow = window as Window & { SpeakSymphony?: SpeakSymphonyBridge };
  hostWindow.SpeakSymphony = bridge;

  const register = () => {
    const client = readSymphonyClient();
    if (!client?.registerClientTool) return;
    for (const action of SYMPHONY_ACTIONS) {
      client.registerClientTool(action.name, action.parameters, (args) => {
        void bridge.dispatch(action.name, args);
      });
    }
    client.registerClientTool(ACCOUNT_CONTEXT_TOOL.name, ACCOUNT_CONTEXT_TOOL.parameters, () => bridge.getAccountContext());
  };
  register();
  bridge.registerClientTools = register;

  const onMessage = (event: MessageEvent) => {
    if (!isAllowedSymphonyMessageOrigin(event.origin, window.location.origin, options.gateway)) return;
    if (!isRequest(event.data)) return;
    const request = event.data;
    const reply = (body: Omit<SpeakResultMessage, "source" | "channel" | "type" | "requestId" | "submitted"> & { submitted?: false }) => {
      const message: SpeakResultMessage = {
        source: "speak",
        channel: SYMPHONY_CHANNEL,
        type: "result",
        requestId: request.requestId,
        submitted: false,
        ...body,
      };
      const target = event.source as { postMessage?: (data: unknown, origin: string) => void } | null;
      if (target?.postMessage) target.postMessage(message, event.origin);
      else window.postMessage(message, event.origin);
    };

    if (request.type === "account.context") {
      void bridge.getAccountContext().then(
        (result) => reply({ ok: true, result }),
        () => reply({ ok: false, error: "account_context_failed" }),
      );
      return;
    }

    void bridge.dispatch(request.name ?? "", request.args).then((result) => {
      const action = result as { ok: boolean; error?: string };
      if (action.ok) reply({ ok: true, result });
      else reply({ ok: false, error: action.error ?? "action_failed", result });
    });
  };

  window.addEventListener("message", onMessage);
  return () => {
    window.removeEventListener("message", onMessage);
    if (hostWindow.SpeakSymphony === bridge) delete hostWindow.SpeakSymphony;
  };
}
